import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  getOutreachCampaign,
  getOutreachOverview,
  getOutreachSignal,
  getUnknownLeadCompanies,
  nameLeadCompany,
  type OutreachWeeks,
  type PlayId,
} from '../api/outreach';

export function useOutreachOverview(weeks: OutreachWeeks) {
  return useQuery({
    queryKey: ['outreach', 'overview', weeks],
    queryFn: () => getOutreachOverview(weeks),
    refetchInterval: 300_000,
  });
}

export function useOutreachSignal(playId: PlayId | null, weeks: OutreachWeeks) {
  return useQuery({
    queryKey: ['outreach', 'signal', playId, weeks],
    queryFn: () => getOutreachSignal(playId as PlayId, weeks),
    enabled: !!playId,
    refetchInterval: 300_000,
  });
}

export function useOutreachCampaign(campaignId: string | null, weeks: OutreachWeeks) {
  return useQuery({
    queryKey: ['outreach', 'campaign', campaignId, weeks],
    queryFn: () => getOutreachCampaign(campaignId as string, weeks),
    enabled: !!campaignId,
    refetchInterval: 300_000,
  });
}

export function useUnknownLeadCompanies() {
  return useQuery({
    queryKey: ['outreach', 'lead-companies', 'unknown'],
    queryFn: getUnknownLeadCompanies,
    refetchInterval: 300_000,
  });
}

export function useNameLeadCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, companyName, companyDomain }: { id: string; companyName: string; companyDomain: string | null }) =>
      nameLeadCompany(id, { companyName, companyDomain }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['outreach', 'lead-companies'] }),
  });
}
